import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Eye, EyeOff, Pencil, Plus, Trash2, Users, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firstAllowedPath } from '../utils/rbacDefaultRoute';
import { canAccessSecurityAdmin } from '../utils/rbacSecurityAccess';
import {
  rbacApi,
  RbacModuleAction,
  RbacModulePermission,
  RbacUserDetail,
  RbacUserRow
} from '../services/rbacApi';
import { showError, showSuccess } from '../utils/notifications';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  getProfilePasswordChecks,
  profileNewPasswordMeetsPolicy,
  type ProfilePasswordRuleCheck
} from '../utils/profilePasswordPolicy';
import './InventoryPage.css';
import './UsersPage.css';

/** Alineado con catálogo RBAC / RolesPage (submódulos por módulo). */
const MODULE_DEFAULT_ACTIONS: Record<string, Array<{ actionKey: string; label: string }>> = {
  xtam_vms: [
    { actionKey: 'streaming', label: 'Ver stream' },
    { actionKey: 'recordings_preview', label: 'Previsualización de grabaciones' },
    { actionKey: 'recordings_download', label: 'Descarga de grabaciones' },
    { actionKey: 'analytics', label: 'Analíticas' }
  ],
  alarmas: [{ actionKey: 'alerts_buttons_eu', label: 'Alertas botón EU' }],
  administracion: [
    { actionKey: 'sites', label: 'Sitios' },
    { actionKey: 'history', label: 'Históricos' },
    { actionKey: 'miniiot', label: 'MiniIOT' },
    { actionKey: 'cameras', label: 'Cámaras' },
    { actionKey: 'inventory', label: 'Inventario' }
  ],
  seguridad: [
    { actionKey: 'roles', label: 'Roles' },
    { actionKey: 'users', label: 'Usuarios' }
  ]
};

function mergeModuleActions(modules: RbacModulePermission[]): RbacModulePermission[] {
  return modules.map((m: RbacModulePermission) => {
    const defs = MODULE_DEFAULT_ACTIONS[m.moduleKey];
    if (!defs) return m;
    const existing = new Map(m.actions.map((a: RbacModuleAction) => [a.actionKey, a]));
    const merged = defs.map((def) => {
      const hit = existing.get(def.actionKey);
      return hit ?? { actionKey: def.actionKey, label: def.label, enabled: false };
    });
    return { ...m, actions: merged };
  });
}

function EffectivePermissionsPanel({ detail }: { detail: RbacUserDetail | null }) {
  if (!detail) {
    return <p className="users-muted">Cargando permisos…</p>;
  }
  if (!detail.user.roleId) {
    return (
      <p className="users-muted">
        Este usuario no tiene rol asignado; no hay módulos ni submódulos efectivos.
      </p>
    );
  }
  if (!detail.effectiveRole) {
    return <p className="users-muted">No se pudo cargar la matriz del rol.</p>;
  }
  const modules = mergeModuleActions(detail.effectiveRole.modules).filter(
    (m: RbacModulePermission) => m.canAccess
  );
  if (modules.length === 0) {
    return <p className="users-muted">El rol no tiene acceso a ningún módulo.</p>;
  }
  return (
    <div className="users-perm-list">
      <p className="users-perm-intro">
        Vista según el rol <strong>{detail.effectiveRole.role.name}</strong> (los permisos se editan en la
        pantalla de Roles).
      </p>
      {modules.map((m: RbacModulePermission) => (
        <div key={m.moduleId} className="users-perm-block">
          <div className="users-perm-module">{m.label}</div>
          <ul className="users-perm-subs">
            {m.actions
              .filter((a: RbacModuleAction) => a.enabled)
              .map((a: RbacModuleAction) => (
                <li key={a.actionKey}>{a.label || a.actionKey}</li>
              ))}
            {m.actions.every((a: RbacModuleAction) => !a.enabled) ? (
              <li className="users-muted">Sin submódulos activos</li>
            ) : null}
          </ul>
        </div>
      ))}
    </div>
  );
}

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState<RbacUserRow[]>([]);
  const [roleOptions, setRoleOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editTarget, setEditTarget] = useState<RbacUserRow | null>(null);
  const [permUserId, setPermUserId] = useState<number | null>(null);
  const [permDetail, setPermDetail] = useState<RbacUserDetail | null>(null);
  const [permLoading, setPermLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RbacUserRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formConfirmPassword, setFormConfirmPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [formUsername, setFormUsername] = useState('');
  const [formRoleId, setFormRoleId] = useState<string>('');
  const [showEditNewPassword, setShowEditNewPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [editPasswordFieldError, setEditPasswordFieldError] = useState<string | null>(null);

  const editNewPasswordChecks = useMemo(
    () => getProfilePasswordChecks(formPassword),
    [formPassword]
  );
  const showEditPasswordChecklist = formMode === 'edit' && formPassword.length > 0;

  useEffect(() => {
    if (user && !canAccessSecurityAdmin(user)) {
      showError('Solo administradores pueden gestionar usuarios');
      navigate(firstAllowedPath(user.allowedModules, false, user.allowedActions), {
        replace: true
      });
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [users, roles] = await Promise.all([rbacApi.getUsers(), rbacApi.getRoleOptions()]);
      setRows(users);
      setRoleOptions(roles);
    } catch (e: any) {
      showError(e.response?.data?.error?.message || e.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (permUserId == null) {
      setPermDetail(null);
      return;
    }
    let cancelled = false;
    setPermLoading(true);
    rbacApi
      .getUser(permUserId)
      .then((d: RbacUserDetail) => {
        if (!cancelled) setPermDetail(d);
      })
      .catch((e: any) => {
        if (!cancelled) {
          showError(e.response?.data?.error?.message || e.message || 'Error al cargar permisos');
          setPermUserId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setPermLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [permUserId]);

  const resetEditPasswordFields = () => {
    setFormConfirmPassword('');
    setAdminPassword('');
    setShowEditNewPassword(false);
    setShowEditConfirmPassword(false);
    setShowAdminPassword(false);
    setEditPasswordFieldError(null);
  };

  const openCreate = () => {
    setEditTarget(null);
    setFormEmail('');
    setFormPassword('');
    setFormUsername('');
    setFormRoleId('');
    resetEditPasswordFields();
    setFormMode('create');
  };

  const openEdit = (r: RbacUserRow) => {
    setEditTarget(r);
    setFormEmail(r.email);
    setFormPassword('');
    setFormUsername(r.username ?? '');
    setFormRoleId(r.roleId != null ? String(r.roleId) : '');
    resetEditPasswordFields();
    setFormMode('edit');
  };

  const closeForm = () => {
    setFormMode(null);
    setEditTarget(null);
    resetEditPasswordFields();
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formMode === 'create') {
      if (!formEmail.trim() || !formPassword) {
        showError('Email y contraseña son obligatorios');
        return;
      }
      setSaving(true);
      try {
        await rbacApi.createUser({
          email: formEmail.trim(),
          password: formPassword,
          username: formUsername.trim() || null,
          roleId: formRoleId === '' ? null : Number(formRoleId)
        });
        showSuccess('Usuario creado');
        closeForm();
        await load();
      } catch (err: any) {
        showError(err.response?.data?.error?.message || err.message || 'Error al crear usuario');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (formMode === 'edit' && editTarget) {
      setEditPasswordFieldError(null);
      const newPwd = formPassword.trim();
      if (newPwd) {
        if (!profileNewPasswordMeetsPolicy(formPassword)) {
          setEditPasswordFieldError(
            'La nueva contraseña no cumple todos los requisitos. Revisa la lista inferior.'
          );
          return;
        }
        if (newPwd !== formConfirmPassword) {
          setEditPasswordFieldError('La confirmación no coincide con la nueva contraseña.');
          return;
        }
      }
      const adminPwd = adminPassword.trim();
      if (!adminPwd) {
        showError('Debes ingresar tu contraseña de administrador para guardar los cambios');
        return;
      }
      setSaving(true);
      try {
        const editingSelf = user?.id === editTarget.id;
        const patch: {
          email?: string;
          username?: string | null;
          roleId?: number | null;
          password?: string;
          adminCurrentPassword: string;
        } = {
          email: formEmail.trim(),
          username: formUsername.trim() || null,
          adminCurrentPassword: adminPwd
        };
        if (!editingSelf) {
          patch.roleId = formRoleId === '' ? null : Number(formRoleId);
        }
        if (newPwd) {
          patch.password = newPwd;
        }
        await rbacApi.updateUser(editTarget.id, patch);
        showSuccess('Usuario actualizado');
        closeForm();
        await load();
      } catch (err: any) {
        const wrongAdmin = err.response?.data?.error?.details?.code === 'WRONG_ADMIN_PASSWORD';
        if (wrongAdmin) {
          showError('Tu contraseña de administrador no es correcta.');
        } else {
          showError(err.response?.data?.error?.message || err.message || 'Error al actualizar usuario');
        }
      } finally {
        setSaving(false);
      }
    }
  };

  const handleRoleChange = async (userId: number, roleIdStr: string) => {
    if (user?.id === userId) return;
    const roleId = roleIdStr === '' ? null : Number(roleIdStr);
    try {
      const detail = await rbacApi.updateUser(userId, { roleId });
      setRows((prev) =>
        prev.map((r) =>
          r.id === userId
            ? {
                ...r,
                roleId: detail.user.roleId,
                roleName: detail.user.roleName
              }
            : r
        )
      );
      showSuccess('Rol actualizado');
    } catch (e: any) {
      showError(e.response?.data?.error?.message || e.message || 'Error al actualizar rol');
      load();
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await rbacApi.deleteUser(deleteTarget.id);
      showSuccess('Usuario eliminado');
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      showError(e.response?.data?.error?.message || e.message || 'Error al eliminar usuario');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (user && !canAccessSecurityAdmin(user)) {
    return null;
  }

  return (
    <div className="users-page">
      <div className="users-header">
        <div className="users-header-row">
          <div>
            <h1>
              <Users size={28} className="users-title-icon" aria-hidden />
              Usuarios
            </h1>
            <p className="users-subtitle">
              CRUD de usuarios; rol, módulos y submódulos efectivos vienen del rol (RBAC).
            </p>
          </div>
          <button type="button" className="users-btn-primary" onClick={openCreate}>
            <Plus size={18} aria-hidden />
            Nuevo usuario
          </button>
        </div>
      </div>

      <div className="users-table-container">
        <div className="users-table-header">
          <h2>Listado de usuarios</h2>
        </div>
        {loading ? (
          <p className="users-table-loading">Cargando…</p>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Usuario</th>
                <th>Activo</th>
                <th>Rol</th>
                <th className="users-th-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.email}</td>
                  <td>{r.username || '—'}</td>
                  <td>{r.active ? 'Sí' : 'No'}</td>
                  <td>
                    {user?.id === r.id ? (
                      <div
                        className="users-role-locked"
                        title="Por seguridad no puedes cambiar tu propio rol desde aquí"
                      >
                        <span className="users-role-locked-name">{r.roleName ?? 'Sin rol'}</span>
                        <span className="users-role-locked-hint">No disponible</span>
                      </div>
                    ) : (
                      <select
                        className="users-role-select"
                        value={r.roleId ?? ''}
                        onChange={(e) => handleRoleChange(r.id, e.target.value)}
                        aria-label={`Rol para ${r.email}`}
                      >
                        <option value="">Sin rol</option>
                        {roleOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    <div className="users-actions">
                      <button
                        type="button"
                        className="users-icon-btn"
                        title="Ver módulos del rol"
                        aria-label={`Permisos de ${r.email}`}
                        onClick={() => setPermUserId(r.id)}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        type="button"
                        className="users-icon-btn"
                        title="Editar"
                        aria-label={`Editar ${r.email}`}
                        onClick={() => openEdit(r)}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        className="users-icon-btn users-icon-btn--danger"
                        title={user?.id === r.id ? 'No puedes eliminarte' : 'Eliminar'}
                        disabled={user?.id === r.id}
                        aria-label={`Eliminar ${r.email}`}
                        onClick={() => setDeleteTarget(r)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formMode && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeForm}>
          <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-header">
              <h2>{formMode === 'create' ? 'Nuevo usuario' : 'Editar usuario'}</h2>
              <button type="button" className="btn-close" onClick={closeForm} aria-label="Cerrar">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="users-form-email">Email</label>
                  <input
                    id="users-form-email"
                    type="email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                {formMode === 'create' ? (
                  <div className="form-group">
                    <label htmlFor="users-form-password">Contraseña</label>
                    <input
                      id="users-form-password"
                      type="password"
                      value={formPassword}
                      onChange={(e) => setFormPassword(e.target.value)}
                      required
                      minLength={6}
                      autoComplete="new-password"
                    />
                  </div>
                ) : null}
                <div className="form-group">
                  <label htmlFor="users-form-username">Nombre de usuario</label>
                  <input
                    id="users-form-username"
                    type="text"
                    value={formUsername}
                    onChange={(e) => setFormUsername(e.target.value)}
                    autoComplete="username"
                  />
                </div>
                {formMode === 'edit' && user?.id === editTarget?.id ? (
                  <div className="form-group">
                    <span className="form-group-label-like">Rol</span>
                    <div
                      className="users-role-locked users-role-locked--form"
                      title="Por seguridad no puedes cambiar tu propio rol"
                    >
                      <span className="users-role-locked-name">
                        {editTarget?.roleName ?? 'Sin rol'}
                      </span>
                      <span className="users-role-locked-hint">No disponible</span>
                    </div>
                  </div>
                ) : (
                  <div className="form-group">
                    <label htmlFor="users-form-role">Rol</label>
                    <select
                      id="users-form-role"
                      value={formRoleId}
                      onChange={(e) => setFormRoleId(e.target.value)}
                      aria-label="Rol"
                    >
                      <option value="">Sin rol</option>
                      {roleOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formMode === 'edit' ? (
                  <>
                    <div className="form-group users-edit-new-pwd">
                      <label htmlFor="users-edit-new-password">Nueva contraseña (opcional)</label>
                      <div className="users-password-input-wrapper">
                        <input
                          id="users-edit-new-password"
                          type={showEditNewPassword ? 'text' : 'password'}
                          value={formPassword}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFormPassword(v);
                            if (!v) setFormConfirmPassword('');
                            if (editPasswordFieldError) setEditPasswordFieldError(null);
                          }}
                          placeholder="Dejar vacío para no cambiar"
                          autoComplete="new-password"
                          className={editPasswordFieldError ? 'users-edit-input--error' : undefined}
                          aria-invalid={Boolean(editPasswordFieldError)}
                          aria-describedby={
                            [
                              showEditPasswordChecklist ? 'users-edit-password-checklist' : '',
                              editPasswordFieldError ? 'users-edit-password-error' : ''
                            ]
                              .filter(Boolean)
                              .join(' ') || undefined
                          }
                        />
                        <button
                          type="button"
                          className="users-password-toggle-btn"
                          onClick={() => setShowEditNewPassword(!showEditNewPassword)}
                          tabIndex={-1}
                          aria-label={showEditNewPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                        >
                          {showEditNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>

                    {showEditPasswordChecklist ? (
                      <div className="form-group users-edit-confirm-pwd">
                        <label htmlFor="users-edit-confirm-password">Confirmar contraseña</label>
                        <div className="users-password-input-wrapper">
                          <input
                            id="users-edit-confirm-password"
                            type={showEditConfirmPassword ? 'text' : 'password'}
                            value={formConfirmPassword}
                            onChange={(e) => {
                              setFormConfirmPassword(e.target.value);
                              if (editPasswordFieldError) setEditPasswordFieldError(null);
                            }}
                            placeholder="Repite la nueva contraseña"
                            autoComplete="new-password"
                            className={editPasswordFieldError ? 'users-edit-input--error' : undefined}
                          />
                          <button
                            type="button"
                            className="users-password-toggle-btn"
                            onClick={() => setShowEditConfirmPassword(!showEditConfirmPassword)}
                            tabIndex={-1}
                            aria-label={
                              showEditConfirmPassword ? 'Ocultar contraseña' : 'Ver contraseña'
                            }
                          >
                            {showEditConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {showEditPasswordChecklist ? (
                      <div
                        id="users-edit-password-checklist"
                        className="users-edit-password-checklist"
                        aria-live="polite"
                      >
                        <p className="users-edit-password-checklist-title">
                          La nueva contraseña debe cumplir con:
                        </p>
                        <ul className="users-edit-password-checklist-items">
                          {editNewPasswordChecks.map((rule: ProfilePasswordRuleCheck) => (
                            <li
                              key={rule.id}
                              className={
                                rule.ok
                                  ? 'users-edit-password-rule users-edit-password-rule--met'
                                  : 'users-edit-password-rule'
                              }
                            >
                              <span className="users-edit-password-rule-icon" aria-hidden>
                                {rule.ok ? (
                                  <Check
                                    className="users-edit-password-check-icon"
                                    size={16}
                                    strokeWidth={2.5}
                                  />
                                ) : (
                                  <span className="users-edit-password-rule-dash">—</span>
                                )}
                              </span>
                              <span className="users-edit-password-rule-text">{rule.label}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {editPasswordFieldError ? (
                      <p
                        id="users-edit-password-error"
                        className="users-edit-password-field-error"
                        role="alert"
                      >
                        {editPasswordFieldError}
                      </p>
                    ) : null}

                    <div className="form-group users-edit-admin-pwd">
                      <label htmlFor="users-edit-admin-password">Tu contraseña de administrador *</label>
                      <p className="users-edit-admin-hint">
                        Se solicita para confirmar que eres quien autoriza el cambio.
                      </p>
                      <div className="users-password-input-wrapper">
                        <input
                          id="users-edit-admin-password"
                          type={showAdminPassword ? 'text' : 'password'}
                          value={adminPassword}
                          onChange={(e) => setAdminPassword(e.target.value)}
                          placeholder="Contraseña de tu cuenta"
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          className="users-password-toggle-btn"
                          onClick={() => setShowAdminPassword(!showAdminPassword)}
                          tabIndex={-1}
                          aria-label={showAdminPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                        >
                          {showAdminPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeForm}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {permUserId != null && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setPermUserId(null)}
        >
          <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-header">
              <h2>Módulos y submódulos (por rol)</h2>
              <button
                type="button"
                className="btn-close"
                onClick={() => setPermUserId(null)}
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {permLoading ? (
                <p className="users-muted">Cargando…</p>
              ) : (
                <EffectivePermissionsPanel detail={permDetail} />
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Eliminar usuario"
        message={
          deleteTarget
            ? `¿Eliminar definitivamente a ${deleteTarget.email}? Esta acción no se puede deshacer.`
            : ''
        }
        confirmText="Eliminar"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteLoading}
      />
    </div>
  );
};

export default UsersPage;
